'use client';

import { Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { SetupConnectFormActions } from '@/components/setup/experience/SetupConnectFormShell';
import { SetupConnectStepSection } from '@/components/setup/experience/SetupConnectStepSection';
import { SETUP_PATH_MOTION } from '@/components/setup/experience/constants';
import { useSetupShippingSummary } from '@/components/setup/experience/hooks/useSetupShippingSummary';
import { mapSetupShippingRateToDraft } from '@/components/setup/experience/lib/setupShippingRateUtils';
import { SetupShippingRatesForm } from '@/components/setup/experience/steps/SetupShippingRatesForm';

interface SetupShippingStepProps {
  storeId: string;
  baseCurrency: string;
  onBack: () => void;
  /** Rates saved (or unchanged) → advance to payment. */
  onContinue: () => void;
}

/**
 * Loads the store's persisted rates, then mounts the form pre-filled with them (a fresh store gets a
 * default rate instead). Gating the mount on the first fetch lets the form seed its defaults from the
 * DB without a `reset()`/effect — the editable cards always reflect what was already created.
 */
export function SetupShippingStep({ storeId, baseCurrency, onBack, onContinue }: SetupShippingStepProps) {
  const { t } = useTranslation(['setup', 'shippings']);
  const xp = 'experience.shipping';

  const freeLabel = t(`${xp}.freeSummary`);
  const allCountriesLabel = t('shippings:table.allCountries');

  const { rates, isLoading } = useSetupShippingSummary(
    storeId,
    baseCurrency,
    freeLabel,
    allCountriesLabel,
    true,
  );

  if (isLoading && rates.length === 0) {
    return (
      <div className="flex flex-col">
        <motion.div
          className="flex w-full flex-col gap-0"
          initial={SETUP_PATH_MOTION.actions.initial}
          animate={SETUP_PATH_MOTION.actions.animate}
          transition={{ ...SETUP_PATH_MOTION.actions.transition, delay: 0.1 }}
        >
          <SetupConnectStepSection
            title={t('experience.shell.stepTitles.shipping')}
            description={t('experience.shell.stepDescriptions.shipping')}
            footer={
              <SetupConnectFormActions
                backLabel={t('footer.back')}
                onBack={onBack}
                submitLabel={t('footer.continue')}
                submitDisabled
              />
            }
          >
            <div className="flex items-center justify-center gap-2.5 py-16" role="status">
              <Loader2 className="text-primary size-5 animate-spin" aria-hidden />
              <span className="text-muted-foreground text-sm">{t(`${xp}.loading`)}</span>
            </div>
          </SetupConnectStepSection>
        </motion.div>
      </div>
    );
  }

  const initialRates = rates.map((rate) => mapSetupShippingRateToDraft(rate, baseCurrency));

  return (
    <SetupShippingRatesForm
      storeId={storeId}
      baseCurrency={baseCurrency}
      initialRates={initialRates}
      hadExistingRates={rates.length > 0}
      onBack={onBack}
      onContinue={onContinue}
    />
  );
}
